import { isBoolean, isNull, isNumber, isString, isUndefined } from "lodash";
import { getLeafValue, getVariationGetter } from "../../helper";
import { VisitNodePassage } from "../../types";
const getNumber = (value) => {
    if (isBoolean(value) || isString(value) || isNull(value) || isUndefined(value)) {
        return null;
    }
    if (isNumber(value)) {
        return value;
    }
    if ("amount" in value) {
        return value.amount;
    }
    return null;
};
var DivisionMethod;
(function (DivisionMethod) {
    DivisionMethod["adjusted"] = "adjusted";
    DivisionMethod["standard"] = "standard";
})(DivisionMethod || (DivisionMethod = {}));
export const divideAdjusted = (top, bottom) => {
    if (bottom && top) {
        return (top - bottom) / Math.abs(bottom);
    }
    else if (bottom === 0 && top === 0) {
        return 0;
    }
    else if (top === 0 && bottom) {
        // - or + 100%
        return Math.sign(bottom) * -1;
    }
    return null;
};
const divideStandard = (top, bottom) => {
    if ((top === 0 || top) && bottom) {
        return top / bottom;
    }
    return null;
};
export const addDivisionValueVisitor = (field, dividend, divisor, method) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.Leaf) {
        return;
    }
    const bottom = getNumber(args.visitable.measures?.[dividend]), top = getNumber(args.visitable.measures?.[divisor]);
    if (args.visitable.measures) {
        switch (method) {
            case DivisionMethod.adjusted:
                args.visitable.measures[field] = divideAdjusted(top, bottom);
                break;
            case DivisionMethod.standard:
                args.visitable.measures[field] = divideStandard(top, bottom);
                break;
            default:
                args.visitable.measures[field] = null;
        }
    }
};
const subtractValues = (left, right) => {
    const amount = (getNumber(left) || 0) - (getNumber(right) || 0), currency = left?.currency || right?.currency;
    if (currency) {
        return { amount, currency };
    }
    return amount;
};
export const addSubstractionValueVisitor = (field, minuend, subtrahend) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.Leaf) {
        return;
    }
    if (args.visitable.measures) {
        const left = args.visitable.measures[minuend], right = args.visitable.measures[subtrahend];
        if (left || left === 0 || right || right === 0) {
            args.visitable.measures[field] = subtractValues(left, right);
        }
    }
};
export const addStrictSubtractionValueVisitor = (field, minuend, subtrahend) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.Leaf) {
        return;
    }
    if (args.visitable.measures) {
        const left = args.visitable.measures[minuend], right = args.visitable.measures[subtrahend];
        if ((left || left === 0) && (right || right === 0)) {
            args.visitable.measures[field] = subtractValues(left, right);
        }
    }
};
export const addMaxValueVisitor = (measures) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let maxValue = Number.MAX_VALUE * -1;
    args.visitable.data.forEach((da) => {
        for (const measure of measures) {
            if (!da.data && (da.measures?.[measure] || da.measures?.[measure] === 0)) {
                const leafValue = Number(getLeafValue(da.measures?.[measure], getVariationGetter(args.context.widgetState?.displayValueType)));
                maxValue = maxValue < leafValue ? leafValue : maxValue;
            }
        }
    });
    if (maxValue || maxValue === 0) {
        if (!args.visitable.additionalData) {
            args.visitable.additionalData = {};
        }
        args.visitable.additionalData.max = maxValue;
    }
};
export const addMinValueVisitor = (measures) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let minValue = Number.MAX_VALUE;
    args.visitable.data.forEach((da) => {
        for (const measure of measures) {
            if (!da.data && (da.measures?.[measure] || da.measures?.[measure] === 0)) {
                const leafValue = Number(getLeafValue(da.measures?.[measure], getVariationGetter(args.context.widgetState?.displayValueType)));
                minValue = minValue > leafValue ? leafValue : minValue;
            }
        }
    });
    if (minValue || minValue === 0) {
        if (!args.visitable.additionalData) {
            args.visitable.additionalData = {};
        }
        args.visitable.additionalData.min = minValue;
    }
};
export const addSumValueVisitor = (measure) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let sumValue = 0;
    args.visitable.data.forEach((da) => {
        if (!da.data && da.measures?.[measure]) {
            sumValue += Number(getLeafValue(da.measures?.[measure]));
        }
    });
    if (sumValue || sumValue === 0) {
        if (!args.visitable.additionalData) {
            args.visitable.additionalData = {};
        }
        args.visitable.additionalData.sum = sumValue;
    }
};
export const addTotalValueVisitor = (measure) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    if (!args.visitable.additionalData) {
        args.visitable.additionalData = {};
    }
    args.visitable.additionalData.sum = Number(getLeafValue(args.visitable.measures?.[measure]));
};
export const addTotalPercentageToLeafVisitor = (measure) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let total = 0;
    args.visitable.data.forEach((da) => {
        if (!da.data && da.measures?.[measure]) {
            total += Number(getLeafValue(da.measures?.[measure]));
        }
    });
    if (total) {
        args.visitable.data.forEach((da) => {
            if (!da.data && da.measures?.[measure]) {
                da.measures.TotalPercentage = Number(getLeafValue(da.measures?.[measure])) / total;
            }
        });
    }
};
export const addCountValueVisitor = (measure) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let count = 0;
    args.visitable.data.forEach((da) => {
        if (!da.data && da.measures?.[measure]) {
            count += 1;
        }
    });
    if (count || count === 0) {
        if (!args.visitable.additionalData) {
            args.visitable.additionalData = {};
        }
        args.visitable.additionalData.count = count;
    }
};
export const addCountValueWithFilterVisitor = (measure, filter) => (args) => {
    if (args?.context?.visitNodePassage !== VisitNodePassage.NodeLast || !args.visitable.data) {
        return;
    }
    let count = 0;
    args.visitable.data.forEach((da) => {
        const value = da.measures?.[measure];
        if (!da.data && value !== undefined && value !== null) {
            if (filter && value.toString() !== filter) {
                return;
            }
            count += 1;
        }
    });
    if (count || count === 0) {
        if (!args.visitable.additionalData) {
            args.visitable.additionalData = {};
        }
        args.visitable.additionalData.count = count;
    }
};
